import { ChangeEvent, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ConfirmModal, { ConfirmModalSecondaryAction } from "../../ConfirmModal";

export interface CatalogueDeleteDialogProps {
  open: boolean;
  /** Full heading, e.g. "ลบสินค้า <code> <name>". */
  title: string;
  /** Code of the product or pricebook; typing it exactly is what unlocks "ลบถาวร". */
  code: string;
  /** One sentence per consequence of the delete, shown as a bullet list. */
  consequences: string[];
  /** Advice under the list, e.g. "ควรลบหลังปิดวัน"; nothing is shown without it. */
  hint?: string;
  deleting: boolean;
  /**
   * The softer alternative, e.g. "ปิดสินค้าแทน". Leave it out to hide the button, as for a
   * row that is already switched off.
   */
  deactivateAction?: ConfirmModalSecondaryAction;
  onClose: () => void;
  onDelete: () => void;
}

/**
 * A catalogue delete cannot be undone and reaches every branch, so the reader has to
 * see what it takes with it and type the code before the delete button unlocks.
 */
export default function CatalogueDeleteDialog({
  open,
  title,
  code,
  consequences,
  hint,
  deleting,
  deactivateAction,
  onClose,
  onDelete,
}: CatalogueDeleteDialogProps) {
  const [typedCode, setTypedCode] = useState<string>("");

  // A code typed for one delete must not unlock the next one.
  useEffect(() => {
    if (open) setTypedCode("");
  }, [open]);

  const handleChangeTypedCode = (event: ChangeEvent<HTMLInputElement>) => {
    setTypedCode(event.target.value);
  };

  const isCodeConfirmed = typedCode === code;

  return (
    <ConfirmModal
      open={open}
      title={title}
      showCloseButton
      confirmLabel="ลบถาวร"
      confirmColor="error"
      confirmDisabled={!isCodeConfirmed}
      loading={deleting}
      secondaryAction={deactivateAction}
      onClose={onClose}
      onConfirm={onDelete}
    >
      <Stack gap={2}>
        <Stack component="ul" gap={0.5} sx={{ pl: 3, m: 0 }}>
          {consequences.map((consequence) => (
            <Typography key={consequence} component="li" variant="body2">
              {consequence}
            </Typography>
          ))}
        </Stack>
        {hint && <Alert severity="warning">{hint}</Alert>}
        <TextField
          size="small"
          fullWidth
          autoFocus
          label={`พิมพ์ ${code} เพื่อยืนยันการลบ`}
          value={typedCode}
          onChange={handleChangeTypedCode}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ mt: 1 }}
        />
      </Stack>
    </ConfirmModal>
  );
}
